/**
 * Cross-tree control channel for the app-shell transcript-ingestion review panel.
 *
 * A transcript-origin ingestion run opens a full-size {@link ReviewPanel} modal
 * overlay. Because the panel must **survive tab switches** (e.g. a provider
 * clicks "Reveal in Blueprint" inside the review — the Documents tab unmounts but
 * the review must persist), it is lifted out of {@link SourcesPanel} and rendered
 * once at the {@link AppContent} level — exactly mirroring how the Care Delivery
 * AI chat sidebar is persisted.
 *
 * {@link AppContent} owns `panelRun` state and provides this context so that
 * descendants (primarily {@link SourcesPanel}) can open the panel imperatively
 * without prop-drilling through DocumentsTab. The default is a no-op so
 * consumers outside a provider still compile and run. Nothing is logged (HIPAA).
 */
import { createContext, useContext } from "react";
import type { IngestionRun } from "../types";

/** The imperative review-control surface exposed to descendants. */
export interface ReviewControl {
  /**
   * Open the full-size transcript-origin review panel for the given run.
   *
   * Called by {@link SourcesPanel}'s `routeReview` for transcript-origin runs.
   * No-op when the panel is already open with the same run.
   */
  openReview: (run: IngestionRun) => void;
}

/** A no-op control used as the default (outside a provider). */
const NOOP_REVIEW_CONTROL: ReviewControl = {
  openReview: () => undefined,
};

/** Context carrying the review-control surface. */
export const ReviewControlContext =
  createContext<ReviewControl>(NOOP_REVIEW_CONTROL);

/**
 * Consume the review-control surface.
 *
 * @returns The {@link ReviewControl} from the nearest provider (or a no-op).
 */
export function useReviewControl(): ReviewControl {
  return useContext(ReviewControlContext);
}
